
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import useLocale from 'next-translate/useTranslation';
import { useMemo } from 'react';
import type { ProductWithPrice } from 'types';
import clsxm from '@/lib/clsxm';
import { getTeamProductsWithPrices } from '@/data/products';
import { tone } from '@/components/agents/primitives';
import { formatMoney, lowestSeatCents, toTeamPlans, } from '@/components/b2b/teamPlans';
import TeamsAdmin from '@/components/b2b/TeamsAdmin';
import TeamsFaq from '@/components/b2b/TeamsFaq';
import TeamsFinalCta from '@/components/b2b/TeamsFinalCta';
import TeamsHero, { type HeroModel } from '@/components/b2b/TeamsHero';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import TeamsPricing from '@/components/b2b/TeamsPricing';
import TeamsReviews from '@/components/b2b/TeamsReviews';
import TeamsSecurity from '@/components/b2b/TeamsSecurity';
import TeamsSharing from '@/components/b2b/TeamsSharing';
import TeamsSuite from '@/components/b2b/TeamsSuite';
import TeamsTraining from '@/components/b2b/TeamsTraining';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
import { type ChatModelProvider, chatModels, FEATURED_CHAT_MODELS, } from '@/constants/chatModels';
interface Props {
    products: ProductWithPrice[];
    heroModels: HeroModel[];
    modelCount: number;
}
/*
 * The page for teams and companies (linked as "B2B Solutions" and
 * "Enterprise"). Story: one workspace with every AI tool, shared by the
 * team, under the owner's control and to IT's standards, with training to
 * get started. Then proof, seat-based pricing and questions.
 */
export default function B2B({ products, heroModels, modelCount }: Props) {
    const { t, lang } = useLocale('b2b');
    // Arabic defaults to Arabic-Indic digits; the page copy uses Latin ones.
    const locale = lang === 'ar' ? 'ar-u-nu-latn' : lang;
    const fromPrice = useMemo(() => {
        const plans = toTeamPlans(products);
        const cents = lowestSeatCents(plans);
        const currency = plans[0]?.prices.month?.currency ?? 'usd';
        return cents === null ? null : formatMoney(cents, currency, locale);
    }, [products, locale]);
    return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main className={clsxm('relative overflow-x-clip', tone.page)} dir={lang === 'ar' ? 'rtl' : undefined}>
      <Seo templateTitle={t('AI for Teams: One AI Workspace for Your Business')} description={t('Give your team the best AI models, Marky Agent, images, video, voice and AI agents in one shared workspace. SOC 2, ISO 27001, GDPR and HIPAA.')} image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t('One AI workspace for your whole team'))}`}/>
      <TeamsHero fromPrice={fromPrice} models={heroModels} modelCount={modelCount}/>
      <TeamsLogos />
      <TeamsSuite />
      <TeamsSharing />
      <TeamsAdmin />
      <TeamsSecurity />
      <TeamsTraining />
      <TeamsReviews />
      <TeamsPricing products={products}/>
      <TeamsFaq />
      <TeamsFinalCta />
      <Footer />
    </main>);
}
// The providers whose newest model the hero lists, in this order.
const HERO_PROVIDERS: ChatModelProvider[] = [
    'OpenAI',
    'Anthropic',
    'Google',
    'xAI',
];
async function getStaticProps() {
    const products = await getTeamProductsWithPrices();
    // Read at build time so the hero names today's models without shipping the
    // whole catalogue to the browser.
    const heroModels: HeroModel[] = HERO_PROVIDERS.flatMap((provider) => {
        const slug = FEATURED_CHAT_MODELS[provider]?.[0]?.slug;
        const model = chatModels.find((item) => item.slug === slug);
        return model ? [{ provider, title: model.title }] : [];
    });
    const visibleModels = chatModels.filter((model) => !model.hidden).length;
    const modelCount = Math.max(10, Math.floor(visibleModels / 10) * 10);
    return {
        props: {
            products,
            heroModels,
            modelCount,
        },
    };
}

    async function __Next_Translate__getStaticProps__1a12e86ec46__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/b2b',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ec46__ as getStaticProps }
  